/**
 * #6402 N2 — the newsletter archive for the public `/news` and `/news/:id` pages.
 * Pure helpers plus two small fetchers, so the logic is unit-tested in node.
 *
 * Data: the www-sbi edge route `/api/news` (src/routes/news-api.ts), which reads the
 * api-sbs-router archive over the API_ROUTER binding and caches it.
 */

export interface NewsIssueSummary {
  id: string;
  subject: string | null;
  sent_at: string | null;
  web_url: string | null;
}

export interface NewsIssue extends NewsIssueSummary {
  text: string | null;
}

export const NEWS_API = '/api/news';
const ID_RE = /^\d{1,24}$/;

/** The issue id of a `/news/:id` path, or null. Pure. */
export function newsIdFromPath(pathname: string): string | null {
  const m = pathname.replace(/\/+$/, '').match(/^\/news\/([^/]+)$/);
  return m && ID_RE.test(m[1]) ? m[1] : null;
}

/** The issue title: the subject, else a neutral fallback. Pure. */
export function issueTitle(issue: Pick<NewsIssueSummary, 'subject'>): string {
  return issue.subject?.trim() || 'SmallBiz News';
}

/**
 * The send date as "October 6, 2026". MailerLite writes "YYYY-MM-DD HH:MM:SS" in the account
 * time zone, so only the date part is read (no time zone shift). Null on a bad value. Pure.
 */
export function formatSentDate(sentAt: string | null): string | null {
  const m = sentAt?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

/** The archive list. An error reads as an empty archive (the page shows its empty state). */
export async function fetchNewsIssues(fetchImpl: typeof fetch = fetch): Promise<NewsIssueSummary[]> {
  try {
    const res = await fetchImpl(NEWS_API);
    if (!res.ok) return [];
    const body = (await res.json()) as { issues?: NewsIssueSummary[] };
    return Array.isArray(body.issues) ? body.issues : [];
  } catch {
    return [];
  }
}

/** One issue, or null when it is missing or not public. */
export async function fetchNewsIssue(id: string, fetchImpl: typeof fetch = fetch): Promise<NewsIssue | null> {
  if (!ID_RE.test(id)) return null;
  try {
    const res = await fetchImpl(`${NEWS_API}/${id}`);
    if (!res.ok) return null;
    const body = (await res.json()) as { issue?: NewsIssue };
    return body.issue ?? null;
  } catch {
    return null;
  }
}
